// frontend/src/motion/motionSignal.js
//
// THE ONE MOTION SWITCH.
//
// Before this module the app had two decoupled "stop moving" mechanisms and
// nothing bridging them:
//
//   1. The in-app accessibility toggle "עצירת אנימציות"
//      (contexts/SimpleAccessibilityContext.js -> localStorage
//      "leadifyai_accessibility_simple" -> body.a11y-no-animations ->
//      styles/simple-accessibility.css). It zeroes CSS animation/transition
//      durations and NOTHING else.
//   2. The OS-level `prefers-reduced-motion` media query, read ad hoc by ~25
//      sx blocks and five `window.matchMedia` call sites.
//
// Neither one stops a requestAnimationFrame loop, a canvas engine, a timer or a
// count-up: a visitor who asked us to stop animating still got a live mesh
// running at 60fps. This module ORs both inputs into a single signal that
// non-React code (rAF engines) and React code (via useMotion) both read.
//
// DELIBERATELY FRAMEWORK-FREE and side-effect-scoped to the document: it imports
// nothing, and it does NOT modify SimpleAccessibilityContext. That context stays
// the single writer of `body.a11y-no-animations` and of its storage key; this
// module only OBSERVES both (MutationObserver on the body class, `storage`
// events for other tabs), so the two can never fight over ownership.
//
// The signal is published three ways, all kept in step:
//   * `html.lf-motion-off`  - one class for CSS to hang off (homepage.css).
//   * `getMotionState()`    - synchronous read for imperative code.
//   * `subscribeMotion(cb)` - change notification, no React coupling.
//
// PAUSE IS SEPARATE FROM OFF. `document.hidden` means "nobody is looking", which
// is a reason to stop BURNING CPU but not a reason to strip animation from the
// page: a background tab that comes back must animate again. So `hidden` feeds
// `paused` only, and `off` (the CSS class, the thing a visitor asked for) never
// depends on it. `paused` is `off || hidden` - the single flag a rAF loop reads.

// The accessibility context's storage key and body class - OBSERVED, never written.
const A11Y_STORAGE_KEY = "leadifyai_accessibility_simple";
const A11Y_BODY_CLASS = "a11y-no-animations";

// Our own published class, written on <html> so CSS can gate a whole subtree.
export const MOTION_OFF_CLASS = "lf-motion-off";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const hasWindow = () => typeof window !== "undefined";
const hasDocument = () => typeof document !== "undefined";

/**
 * Is the in-app "stop animations" toggle on?
 *
 * Two sources, OR'd, because they are true at different moments:
 *  - the body class is the live signal, written by the accessibility context's
 *    effect (and removed when that provider unmounts);
 *  - localStorage is the signal BEFORE React mounts and after a change made in
 *    another tab, which is the whole reason the first paint does not animate.
 *
 * OR is the fail-closed direction: a disagreement between the two resolves to
 * "the visitor asked us to stop", never to "animate anyway".
 *
 * @returns {boolean}
 */
function readA11yDisabled() {
  if (hasDocument() && document.body?.classList?.contains(A11Y_BODY_CLASS)) {
    return true;
  }
  try {
    const raw = hasWindow() ? window.localStorage.getItem(A11Y_STORAGE_KEY) : null;
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.disableAnimations === true;
  } catch {
    // Private mode, blocked storage, or a corrupt value. Absence of a stored
    // preference is not a preference - fall through to the body class alone.
    return false;
  }
}

/** @returns {boolean} the OS-level reduced-motion preference. */
function readReducedMotion() {
  if (!hasWindow() || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches === true;
  } catch {
    return false;
  }
}

/** @returns {boolean} true while the tab is not being looked at. */
function readHidden() {
  return hasDocument() ? document.visibilityState === "hidden" : false;
}

/**
 * @typedef {object} MotionState
 * @property {boolean} off       The visitor asked for no motion (a11y toggle OR OS preference).
 * @property {boolean} paused    `off` OR the tab is hidden - what a rAF loop reads.
 * @property {boolean} hidden    The tab is hidden.
 * @property {boolean} reduced   The OS-level `prefers-reduced-motion` preference.
 * @property {boolean} a11yOff   The in-app accessibility toggle.
 */

// LAZILY computed, never at import time. Module import order is not something
// this file controls - under jest it happens before the test arranges
// matchMedia, and in the browser it can happen before <body> exists - so a
// value computed at import is a value computed against the wrong environment,
// and it would then be handed out forever. `null` means "not read yet".
/** @type {MotionState|null} */
let state = null;

const listeners = new Set();
let wired = false;

/** Reads every input and builds a fresh, frozen state object. */
function compute() {
  const a11yOff = readA11yDisabled();
  const reduced = readReducedMotion();
  const hidden = readHidden();
  const off = a11yOff || reduced;
  return Object.freeze({ off, paused: off || hidden, hidden, reduced, a11yOff });
}

const sameState = (a, b) =>
  a.off === b.off &&
  a.paused === b.paused &&
  a.hidden === b.hidden &&
  a.reduced === b.reduced &&
  a.a11yOff === b.a11yOff;

/** Mirrors `state.off` onto <html> so CSS has one selector to gate on. */
function publishClass() {
  if (!hasDocument() || !document.documentElement) return;
  document.documentElement.classList.toggle(MOTION_OFF_CLASS, state.off);
}

/**
 * Re-read every input; notify subscribers only if something actually changed.
 *
 * Exported because a caller that mutates an input synchronously (a test, or code
 * that flips the body class itself) should not have to wait for a
 * MutationObserver microtask to see a consistent signal.
 *
 * @returns {MotionState} the current state.
 */
export function refreshMotionState() {
  if (state === null) {
    ensureInit();
    return state;
  }
  const next = compute();
  if (sameState(next, state)) {
    // Still publish: the class can be missing on the very first call, or have
    // been stripped by something else, while the computed value is unchanged.
    publishClass();
    return state;
  }
  state = next;
  publishClass();
  listeners.forEach((cb) => {
    try {
      cb(state);
    } catch {
      // One bad subscriber must not stop the rest from being told.
    }
  });
  return state;
}

/**
 * First read: compute the state, publish it, then install the listeners.
 *
 * Order matters - `wire()` can call back into `refreshMotionState()`
 * synchronously, which needs a state to compare against.
 */
function ensureInit() {
  if (state !== null) return;
  state = compute();
  publishClass();
  wire();
}

/** Installs the input listeners once, lazily, on first read or subscribe. */
function wire() {
  if (wired || !hasWindow()) return;
  wired = true;

  // OS preference.
  if (typeof window.matchMedia === "function") {
    try {
      const mq = window.matchMedia(REDUCED_MOTION_QUERY);
      const onChange = () => refreshMotionState();
      if (typeof mq.addEventListener === "function") {
        mq.addEventListener("change", onChange);
      } else if (typeof mq.addListener === "function") {
        mq.addListener(onChange); // Safari < 14
      }
    } catch {
      /* matchMedia present but unusable - the initial read already defaulted off */
    }
  }

  if (hasDocument()) {
    // The accessibility toggle, as written by SimpleAccessibilityContext. The
    // context REPLACES body.className wholesale, so observe the attribute
    // rather than trying to hook a classList method.
    if (typeof MutationObserver === "function" && document.body) {
      try {
        new MutationObserver(() => refreshMotionState()).observe(document.body, {
          attributes: true,
          attributeFilter: ["class"],
        });
      } catch {
        /* no observer - the storage listener and refreshMotionState still work */
      }
    }
    document.addEventListener("visibilitychange", () => refreshMotionState());
  }

  // The same preference changed in another tab.
  window.addEventListener("storage", (event) => {
    if (!event || event.key === null || event.key === A11Y_STORAGE_KEY) {
      refreshMotionState();
    }
  });
}

/**
 * Current motion state. Stable object identity between changes, so it is safe
 * to hand straight to `useSyncExternalStore`.
 *
 * @returns {MotionState}
 */
export function getMotionState() {
  ensureInit();
  return state;
}

/**
 * Subscribe to motion-state changes.
 *
 * @param {(state: MotionState) => void} callback invoked on every change.
 * @returns {() => void} unsubscribe.
 */
export function subscribeMotion(callback) {
  if (typeof callback !== "function") return () => {};
  ensureInit();
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/**
 * Test-only reset: drops every subscriber and re-reads the inputs.
 *
 * The wiring itself is deliberately NOT undone - jsdom tears the document down
 * between files, and a half-wired module is a worse state than a wired one.
 */
export function __resetMotionSignalForTests() {
  listeners.clear();
  state = null;
  // Re-read AND re-wire: `wire()` is idempotent, and going through ensureInit
  // is what guarantees the listeners exist after a reset. Setting `state`
  // directly here would short-circuit ensureInit and leave the module deaf.
  ensureInit();
}
